import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    // Capture phase: open before other cart:update listeners (e.g. section morph).
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, { capture: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#syncEmptyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd, { capture: true });
    document.removeEventListener(ThemeEvents.cartUpdate, this.#syncEmptyState);
  }

  /**
   * Keeps the drawer empty-state class in sync when the cart is updated via AJAX.
   * @param {CustomEvent} event
   */
  #syncEmptyState = (event) => {
    const itemCount = event.detail?.data?.itemCount;
    if (itemCount === undefined) return;

    this.refs.dialog?.classList.toggle('cart-drawer--empty', itemCount === 0);
  };

  /**
   * @param {CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (event.detail?.data?.didError) return;
    // Only auto-open when a product is added — not on quantity changes or removals.
    if (event.detail?.data?.source !== 'product-form-component') return;

    const openDrawer = () => {
      if (!this.refs.dialog.open) {
        this.showDialog({ immediate: true });
      }
    };

    // When adding from an open modal (e.g. quick-add), wait for it to close first.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Ignore the cart drawer itself — otherwise removing the last item registers a
    // close listener that reopens the drawer when the customer dismisses it.
    if (sourceModal?.open && sourceModal !== this.refs.dialog) {
      sourceModal.addEventListener('close', openDrawer, { once: true });
      return;
    }

    openDrawer();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
